import { FIXED_LAYOUT_GRID, SLOT_COUNT, type SceneLayout } from "./scene/types";

const VIDEO_RATIO = 16 / 9;

export const LOW_STREAM_WIDTH = 640;

export interface GridDimensions {
  columns: number;
  rows: number;
}

/** 가로·세로 칸 수가 모두 짝수여야 바둑판 가운데에 칸 사이가 온다 — 그래야 베젤을 피한다. */
function splitsAtCenter({ columns, rows }: GridDimensions) {
  return columns % 2 === 0 && rows % 2 === 0;
}

const SCREEN_FIT_DEFAULT: GridDimensions = FIXED_LAYOUT_GRID["4x4"];
const SCREEN_FIT_MIN = SLOT_COUNT["4x4"];
const FULLER_TOLERANCE = 0.1;

export function screenFitDimensions(
  width: number,
  height: number,
  gap: number,
  count = 0
): GridDimensions {
  const needed = Math.min(Math.max(count, SCREEN_FIT_MIN), SLOT_COUNT.auto);
  let best: GridDimensions | null = null;
  let bestWidth = 0;
  for (let columns = 2; columns <= SLOT_COUNT.auto / 2; columns += 2) {
    for (let rows = 2; columns * rows <= SLOT_COUNT.auto; rows += 2) {
      if (columns * rows < needed) continue;
      const tileWidth = Math.min(
        (width - gap * (columns - 1)) / columns,
        ((height - gap * (rows - 1)) / rows) * VIDEO_RATIO
      );
      const larger = tileWidth > bestWidth * (1 + FULLER_TOLERANCE);
      const fuller =
        best != null &&
        tileWidth >= bestWidth * (1 - FULLER_TOLERANCE) &&
        columns * rows > best.columns * best.rows;
      if (best == null || larger || fuller) {
        best = { columns, rows };
        bestWidth = tileWidth;
      }
    }
  }
  return best ?? SCREEN_FIT_DEFAULT;
}

export function cameraGridDimensions(layout: SceneLayout) {
  const { columns, rows } = layout === "auto" ? SCREEN_FIT_DEFAULT : FIXED_LAYOUT_GRID[layout];
  return { columns, rows, count: columns * rows, label: `${columns}×${rows}` };
}

export function fitCameraGrid(
  layout: SceneLayout,
  width: number,
  height: number,
  gap: number,
  autoDimensions?: GridDimensions,
  /** 칸마다 영상 아래에 붙는 정보 줄 높이(px). */
  caption = 0
) {
  const { columns, rows } =
    layout === "auto" && autoDimensions ? autoDimensions : cameraGridDimensions(layout);
  const tileWidth = Math.max(
    0,
    Math.min(
      (width - gap * (columns - 1)) / columns,
      ((height - gap * (rows - 1)) / rows - caption) * VIDEO_RATIO
    )
  );
  return {
    columns,
    rows,
    tileWidth,
    width: tileWidth * columns + gap * (columns - 1),
    height: (tileWidth / VIDEO_RATIO + caption) * rows + gap * (rows - 1),
  };
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * 관제실 비디오월(모니터 4대를 NVIDIA Mosaic 으로 묶은 2×2) 바둑판.
 *
 * 바둑판을 남은 영역 가운데에 두면 헤더·좌우 패널 폭만큼 중심이 밀려, 모니터가 만나는
 * 베젤 십자가 영상 한가운데를 지나간다. 그래서 바둑판 가운데를 뷰포트 가운데(=베젤 십자)에
 * 고정하고, 그 점에서 헤더·패널까지 가장 가까운 거리 안에 들어가게 줄인다. 가로·세로 칸
 * 수가 짝수면(2×2·4×4·6×6·짝수 화면 맞춤) 가운데 칸 사이가 베젤 위에 오고 모든 칸 크기가 같다.
 * 짝수가 아니면 가운데에 칸 사이가 없어 `null`.
 *
 * 가운데에 묶은 탓에 위아래(또는 좌우)에 여백이 남는다. 그 여백에 `caption` 높이의 정보 줄이
 * 들어가면 영상 아래로 내린다(`caption: true`). 줄을 넣느라 영상이 작아질 때는 넣지 않는다.
 *
 * `container` 는 뷰포트 좌표, 반환하는 `x`·`y` 는 `container` 기준 좌표다. 키오스크로 띄워
 * 뷰포트가 화면 전체를 덮어야 뷰포트 가운데가 실제 베젤과 맞는다.
 */
export function wallCameraGrid(
  layout: SceneLayout,
  container: Rect,
  viewport: { width: number; height: number },
  gap: number,
  caption: number,
  autoDimensions?: GridDimensions
) {
  const dimensions =
    layout === "auto" && autoDimensions ? autoDimensions : cameraGridDimensions(layout);
  if (!splitsAtCenter(dimensions)) return null;
  const centerX = viewport.width / 2 - container.x;
  const centerY = viewport.height / 2 - container.y;
  const halfWidth = Math.max(0, Math.min(centerX, container.width - centerX));
  const halfHeight = Math.max(0, Math.min(centerY, container.height - centerY));
  const plain = fitCameraGrid(layout, halfWidth * 2, halfHeight * 2, gap, autoDimensions);
  const captioned = fitCameraGrid(
    layout,
    halfWidth * 2,
    halfHeight * 2,
    gap,
    autoDimensions,
    caption
  );
  const fits = captioned.tileWidth >= plain.tileWidth - 0.5;
  const grid = fits ? captioned : plain;
  return {
    ...grid,
    caption: fits,
    x: centerX - grid.width / 2,
    y: centerY - grid.height / 2,
  };
}

/** 칸 좌표 — 도면처럼 열은 A·B·C…, 행은 1·2·3…. "B3 카메라" 처럼 말로 가리키게 한다. */
export function slotCoordinate(index: number, columns: number) {
  return `${String.fromCharCode(65 + (index % columns))}${Math.floor(index / columns) + 1}`;
}

export function gridStreamQuality(tileWidth: number, pixelRatio: number): "high" | "low" {
  return tileWidth * pixelRatio > LOW_STREAM_WIDTH ? "high" : "low";
}
